import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { endpoints } from "../lib/api";
import { getAll, put, queueSync, STORES } from "../lib/db";
import SignatureModal from "../components/SignatureModal";
import {
    FileText, Signature, ArrowRight, Plus, Copy, CheckCircle, PaperPlaneTilt,
    ChatCircleText, Eye, ArrowSquareOut, Funnel, X,
} from "@phosphor-icons/react";

const COLUMNS = [
    { key: "draft", label: "Draft" },
    { key: "sent", label: "Sent" },
    { key: "viewed", label: "Viewed" },
    { key: "signed", label: "Signed" },
];

const PROPOSAL_COLS = [
    { key: "sent", label: "Sent · awaiting", icon: <PaperPlaneTilt size={13} weight="bold" />, accent: "var(--rom-orange)" },
    { key: "viewed", label: "Viewed", icon: <Eye size={13} weight="bold" />, accent: "var(--rom-navy)" },
    { key: "changes_requested", label: "Changes requested", icon: <ChatCircleText size={13} weight="bold" />, accent: "var(--rom-orange-dark)" },
    { key: "accepted", label: "Accepted & signed", icon: <CheckCircle size={13} weight="fill" />, accent: "var(--rom-green)" },
];

const RECIP_LABEL = { adjuster: "Adjuster", insurer: "Insurance", client: "Client" };
function proposalShareUrl(token) { return `${window.location.origin}/share/${token}`; }

export default function Documents() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const focus = searchParams.get("focus"); // "changes_requested" | "awaiting" | null
    const [docs, setDocs] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [signing, setSigning] = useState(null);
    const [copied, setCopied] = useState(null);

    useEffect(() => { reload(); }, []);
    useEffect(() => {
        (async () => {
            try {
                await endpoints.markProposalAlertsSeen();
                window.dispatchEvent(new Event("proposals-seen"));
            } catch { /* offline / optional */ }
        })();
    }, []);
    async function reload() {
        try {
            const [d, j] = await Promise.all([endpoints.docs(), endpoints.jobs()]);
            setDocs(d.data || []);
            setJobs(j.data || []);
        } catch {
            setDocs(await getAll(STORES.documents));
            setJobs(await getAll(STORES.jobs));
        }
    }

    async function saveDoc(doc) {
        await put(STORES.documents, doc);
        try { await endpoints.saveDoc(doc); }
        catch { await queueSync("documents", doc); }
        await reload();
    }

    async function newDocFromJob(j) {
        const doc = {
            id: crypto.randomUUID(),
            job_id: j.id,
            title: `ROM ${j.ref} · ${j.loss_type}`,
            kind: "rom",
            status: "draft",
            payload: { ref: j.ref, total: j.result?.grand_total },
            created_by: user?.id,
            updated_at: new Date().toISOString(),
        };
        await saveDoc(doc);
    }

    async function move(doc, status) {
        await saveDoc({ ...doc, status, updated_at: new Date().toISOString() });
    }

    async function signDoc(doc, sig) {
        const updated = { ...doc, signature: sig, status: "signed", updated_at: new Date().toISOString() };
        await put(STORES.documents, updated);
        try { await endpoints.signDoc(doc.id, sig); }
        catch { await queueSync("documents", updated); }
        await reload();
    }

    function copyLink(token) {
        navigator.clipboard?.writeText(proposalShareUrl(token));
        setCopied(token);
        setTimeout(() => setCopied(null), 2000);
    }

    const proposals = docs.filter((d) => d.kind === "rom_proposal");
    const otherDocs = docs.filter((d) => d.kind !== "rom_proposal" && d.kind !== "invoice");
    const focusedKeys = focus === "awaiting" ? ["sent", "viewed"] : focus === "changes_requested" ? ["changes_requested"] : [];
    const focusedCount = proposals.filter((d) => focusedKeys.includes(d.status)).length;
    const focusLabel = focus === "awaiting" ? "Showing proposals awaiting a response" : focus === "changes_requested" ? "Showing proposals with changes requested" : "";

    return (
        <div className="max-w-[1600px] mx-auto px-6 py-8">
            <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">Documents</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <FileText size={28} weight="bold" className="text-[var(--rom-orange)]" />
                        Sign-off board
                    </h1>
                </div>
            </header>

            {focus && (
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-[6px] border border-[#fdd2b2] bg-[var(--rom-orange-soft)]/50 text-sm mb-6" data-testid="docs-focus-banner">
                    <Funnel size={14} weight="bold" className="text-[var(--rom-orange-dark)]" />
                    <span className="font-semibold text-[var(--rom-orange-dark)]">{focusLabel}</span>
                    <span className="text-[var(--rom-text-3)]">({focusedCount})</span>
                    <button onClick={() => setSearchParams({})} className="ml-auto text-xs font-semibold text-[var(--rom-orange-dark)] hover:underline flex items-center gap-1" data-testid="docs-clear-focus-btn"><X size={12} weight="bold" /> Clear filter</button>
                </div>
            )}

            {/* Shared proposals pipeline — every Send-to-Adjuster/Insurance/Client link & its recipient status */}
            <section className="mb-8" data-testid="proposals-board">
                <div className="flex items-baseline justify-between gap-3 mb-3">
                    <h2 className="font-display font-bold text-lg flex items-center gap-2">
                        <PaperPlaneTilt size={18} weight="bold" className="text-[var(--rom-orange)]" /> Shared proposals
                    </h2>
                    <span className="text-xs text-[var(--rom-text-2)]">{proposals.length} sent · track views, acceptance & change requests</span>
                </div>
                {proposals.length === 0 ? (
                    <div className="rom-card"><div className="rom-card-body text-sm text-[var(--rom-text-2)] text-center py-8">
                        No proposals shared yet. Open a ROM, compute it, then use <b>Actions → Send to Adjuster / Insurance / Client</b> to generate a secure link.
                    </div></div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {PROPOSAL_COLS.map((col) => {
                            const items = proposals.filter((d) => d.status === col.key);
                            const highlighted = focusedKeys.includes(col.key);
                            return (
                                <div key={col.key} className={`rom-card bg-slate-50 transition-shadow ${highlighted ? "ring-2 ring-offset-1" : ""}`} style={highlighted ? { boxShadow: `0 0 0 2px ${col.accent}` } : undefined} data-testid={`prop-col-${col.key}`}>
                                    <div className="rom-card-head bg-white" style={{ borderTop: `3px solid ${col.accent}` }}>
                                        <h3 className="flex items-center gap-1.5" style={{ color: col.accent }}>{col.icon} {col.label} <span className="text-[var(--rom-text-2)] ml-1">({items.length})</span></h3>
                                    </div>
                                    <div className="p-3 space-y-2 min-h-[160px]">
                                        {items.length === 0 && <div className="text-xs text-[var(--rom-text-3)] text-center py-6">Empty</div>}
                                        {items.map((d) => {
                                            const p = d.payload || {};
                                            const rt = RECIP_LABEL[p.recipient_type] || "Client";
                                            return (
                                                <div key={d.id} className="bg-white border border-[var(--rom-border)] rounded-[4px] p-3 text-sm" data-testid={`prop-${d.id}`}>
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="font-mono text-[11px] font-semibold text-[var(--rom-text-2)]">{p.ref || "ROM"}</span>
                                                        <span className="chip chip-navy" style={{ padding: "1px 6px" }}>{rt}</span>
                                                    </div>
                                                    <div className="font-semibold mt-1">{p.recipient_name || rt}</div>
                                                    {p.grand_total > 0 && <div className="font-mono text-[var(--rom-text)] mt-0.5">${Number(p.grand_total).toLocaleString()}</div>}
                                                    {d.status === "changes_requested" && (p.change_request?.message || p.change_request?.items?.length > 0) && (
                                                        <div className="mt-2 text-[11px] text-[var(--rom-orange-dark)] bg-[var(--rom-orange-soft)]/60 rounded px-2 py-1.5 space-y-1" data-testid={`prop-change-${d.id}`}>
                                                            {p.change_request.requested_by && <div className="font-semibold">{p.change_request.requested_by} requested changes:</div>}
                                                            {(p.change_request.items || []).map((it, i) => (
                                                                <div key={i} className="flex flex-wrap items-baseline gap-x-1.5">
                                                                    <span className="chip chip-orange" style={{ padding: "0px 5px" }}>{it.tag}</span>
                                                                    <b>{it.label}</b>
                                                                    {it.suggested_amount != null && <span className="font-mono">→ suggests ${Number(it.suggested_amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                                                                    {it.comment && <span className="italic">“{it.comment}”</span>}
                                                                </div>
                                                            ))}
                                                            {p.change_request.message && <div><b>Note:</b> {p.change_request.message}</div>}
                                                        </div>
                                                    )}
                                                    {d.status === "accepted" && p.acceptance?.accepted_by && (
                                                        <div className="mt-2 text-[11px] text-[var(--rom-green)] flex items-center gap-1">
                                                            <CheckCircle size={12} weight="fill" /> Signed by {p.acceptance.accepted_by}
                                                        </div>
                                                    )}
                                                    <div className="flex flex-wrap gap-1 mt-3">
                                                        <button onClick={() => copyLink(d.share_link)} className="btn btn-ghost btn-sm" data-testid={`prop-copy-${d.id}`}>
                                                            {copied === d.share_link ? <CheckCircle size={12} weight="bold" className="text-[var(--rom-green)]" /> : <Copy size={12} weight="bold" />}
                                                            {copied === d.share_link ? "Copied" : "Copy link"}
                                                        </button>
                                                        {d.job_id && (
                                                            <button onClick={() => navigate(`/calculator/${d.job_id}`)} className="btn btn-ghost btn-sm" data-testid={`prop-open-${d.id}`}>
                                                                <ArrowSquareOut size={12} weight="bold" /> Open ROM
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {jobs.length > 0 && (
                <div className="rom-card mb-6">
                    <div className="rom-card-head"><h3><Plus size={14} weight="bold" /> Create document from job</h3></div>
                    <div className="rom-card-body flex flex-wrap gap-2">
                        {jobs.slice(0, 8).map((j) => (
                            <button key={j.id} onClick={() => newDocFromJob(j)} className="px-3 py-1.5 text-xs font-semibold border border-[var(--rom-border-strong)] rounded-[4px] hover:border-[var(--rom-navy)]" data-testid={`doc-from-${j.ref}`}>
                                {j.ref} · {j.loss_type}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {COLUMNS.map((col) => {
                    const items = otherDocs.filter((d) => d.status === col.key);
                    return (
                        <div key={col.key} className="rom-card bg-slate-50" data-testid={`col-${col.key}`}>
                            <div className="rom-card-head bg-white">
                                <h3>{col.label} <span className="text-[var(--rom-text-2)] ml-1">({items.length})</span></h3>
                            </div>
                            <div className="p-3 space-y-2 min-h-[200px]">
                                {items.length === 0 && <div className="text-xs text-[var(--rom-text-3)] text-center py-6">Empty</div>}
                                {items.map((d) => {
                                    const nextStatus = nextOf(d.status);
                                    return (
                                        <div key={d.id} className="bg-white border border-[var(--rom-border)] rounded-[4px] p-3 text-sm" data-testid={`doc-${d.id}`}>
                                            <div className="font-semibold">{d.title}</div>
                                            <div className="text-[11px] text-[var(--rom-text-2)] mt-1">
                                                {d.payload?.total ? `$${Number(d.payload.total).toLocaleString()}` : ""}
                                                {d.signature && <span className="ml-2 chip chip-green">Signed</span>}
                                            </div>
                                            <div className="flex gap-1 mt-3">
                                                {nextStatus && (
                                                    <button onClick={() => move(d, nextStatus)} className="btn btn-ghost btn-sm" data-testid={`doc-advance-${d.id}`}>
                                                        → {nextStatus} <ArrowRight size={12} weight="bold" />
                                                    </button>
                                                )}
                                                {col.key !== "signed" && (
                                                    <button onClick={() => setSigning(d)} className="btn btn-primary btn-sm" data-testid={`doc-sign-${d.id}`}>
                                                        <Signature size={12} weight="bold" /> Sign
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            <SignatureModal
                open={!!signing}
                onClose={() => setSigning(null)}
                onAccept={(sig) => signing && signDoc(signing, sig)}
                title={`Sign · ${signing?.title || ""}`}
                signerName={user?.name}
            />
        </div>
    );
}

function nextOf(s) { return ({ draft: "sent", sent: "viewed", viewed: "signed" })[s] || null; }
